<script setup lang="ts">
import {
  ref,
  computed,
  nextTick,
  onMounted,
  onBeforeUnmount,
  useTemplateRef,
} from "vue";
import { storeToRefs } from "pinia";
import { WindowScroller } from "vue-virtual-scroller";
import { users } from "/api";
import { i18n as I18n } from "/locales/i18n";
import { useUserListStore } from "/stores/user_list_store";
import UserCard from "./user_card.vue";
import InfotainmentCard from "./infotainment_card.vue";
import "vue-virtual-scroller/dist/vue-virtual-scroller.css";
import type { Infotainment } from "/types/infotainment";
import type * as UserList from "./types/user_list";

defineOptions({
  name: "UserList",
});

type ListItem =
  | { type: "user"; data: UserList.User; key: string }
  | { type: "infotainment"; data: Infotainment; key: string };

const props = defineProps({
  desktopBreakpoint: { type: String, default: "(min-width: 750px)" },
  usersPerInfotainment: { type: Number, default: 6 },
  gap: { type: Number, default: 10 },
  // start fetching the next page when there are this many items left below the rendered range
  loadAheadItems: { type: Number, default: 10 },
  // pixels of off-screen content to render — preloads images before cards enter the viewport
  scrollerBuffer: { type: Number, default: 1500 },
  // pixels deducted from the hash-scroll target so the item lands below the sticky header
  scrollOffset: { type: Number, default: 65 },
});

const containerRef = useTemplateRef("containerRef");
const scrollerRef = useTemplateRef("scrollerRef");
const loading = ref(false);
const initialized = ref(false);

const store = useUserListStore();
const {
  users: userList,
  infotainments: infotainmentList,
  page,
  hasMore,
  so: sObject,
  restrict,
} = storeToRefs(store);

// Interleave one infotainment every `usersPerInfotainment` users. infotainmentList keys include the
// flat-array index because the same infotainment id can show up across reloads (server RANDs them).
const items = computed(() => {
  const out: ListItem[] = [];
  let infoIdx = 0;
  userList.value.forEach((user, i) => {
    if (i > 0 && i % props.usersPerInfotainment === 0) {
      const info = infotainmentList.value[infoIdx];
      if (info) {
        out.push({
          type: "infotainment",
          data: info,
          key: `i-${infoIdx}-${info.id}`,
        });
      }
      infoIdx += 1;
    }
    out.push({ type: "user", data: user, key: `u-${user.id}` });
  });
  return out;
});

function urlQuery(): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of new URLSearchParams(window.location.search)) out[k] = v;
  return out;
}

function getHashAnchorUserId(): number | null {
  const m = window.location.hash.match(/^#si-(\d+)$/);
  return m ? Number(m[1]) : null;
}

// Cache restore semantics: `back=true` is the marker addAnchorToCurrentState sets when leaving
// for a profile show, so we treat it as the "return from show" signal. If the URL pins an `so`
// (e.g. shared link) we require it to match. The anchor check guards against a cache that
// doesn't contain the user we'd scroll to.
function shouldRestoreFromStore() {
  if (urlQuery().back !== "true") return false;
  if (store.users.length === 0) return false;
  const urlSo = urlQuery().so;
  if (urlSo && urlSo !== store.so) return false;
  const anchorId = getHashAnchorUserId();
  if (anchorId && !store.hasUserId(anchorId)) return false;
  return true;
}

// Keep `?page=N` in sync with the highest loaded page so a browser-back from a profile restores the full scroll range
function syncPageInUrl() {
  if (page.value <= 1) return;
  const url = new URL(window.location.href);
  if (url.searchParams.get("page") === String(page.value)) return;
  url.searchParams.set("page", String(page.value));
  (window as any).Turbo.navigator.history.update(history.replaceState, url);
}

function applyResponse(data: UserList.Response) {
  page.value = data.page;
  hasMore.value = data.hasMore;
  sObject.value = data.sObject || "";
  if (data.restrict) restrict.value = data.restrict;
  userList.value.push(...(data.users || []));
  infotainmentList.value.push(...(data.infotainments || []));
  syncPageInUrl();
}

async function load(query) {
  if (loading.value) return;
  loading.value = true;
  try {
    const data = await users.index<UserList.Response>({ query });
    if (!data || !Array.isArray(data.users)) {
      throw new Error(`Invalid UserList response: ${JSON.stringify(data)}`);
    }
    applyResponse(data);
  } catch (e) {
    console.error("UserList: failed to load", e);
  } finally {
    loading.value = false;
  }
}

function loadMore() {
  if (!hasMore.value) return;
  return load({ page: page.value + 1, so: sObject.value || undefined });
}

async function scrollToHashItem() {
  const match = window.location.hash.match(/^#si-(\d+)$/);
  if (!match) return;
  const userId = Number(match[1]);
  const idx = items.value.findIndex(
    (it) => it.type === "user" && it.data.id === userId,
  );
  if (idx < 0) return;
  if (idx >= items.value.length - props.loadAheadItems) {
    await loadMore();
    await nextTick();
  }
  scrollerRef.value?.scrollToItem(idx, {
    offset: -props.scrollOffset,
  });
}

// --- grid sizing ---
const containerWidth = ref(0);
const mql = window.matchMedia(props.desktopBreakpoint);
const gridItems = ref(mql.matches ? 2 : 1);
const onMqlChange = (e: MediaQueryListEvent) =>
  (gridItems.value = e.matches ? 2 : 1);
let resizeObserver: ResizeObserver | null = null;

function measureContainer() {
  const w = containerRef.value?.clientWidth ?? 0;
  if (w) containerWidth.value = w;
}

const itemSecondarySize = computed(() =>
  containerWidth.value ? Math.floor(containerWidth.value / gridItems.value) : 0,
);
// inner card size after the cell padding (gap)
const cardInnerWidth = computed(() => Math.max(0, itemSecondarySize.value));

// user-card has aspect-ratio 3/4 → height = width * 4/3
// (viewport - padding*2) * (4/3) + (padding-top*columns)
// ITEM = (430 - 10*2) * (4/3) + 10*1 = 557
// CARD = 410x547
// 410=(430-10*2)
// 547=(557 - 10)
const itemSize = computed(() =>
  itemSecondarySize.value
    ? Math.round(
        (cardInnerWidth.value - props.gap * 2) * (4 / 3) +
          props.gap * gridItems.value,
      )
    : 0,
);

function onScrollerUpdate(_startIndex: number, endIndex: number) {
  if (endIndex >= items.value.length - props.loadAheadItems) loadMore();
}

onMounted(async () => {
  mql.addEventListener("change", onMqlChange);
  measureContainer();
  // rAF wrap: avoids "ResizeObserver loop completed with undelivered notifications" when the
  // measure triggers a re-layout (item size depends on containerWidth) within the same frame.
  resizeObserver = new ResizeObserver(() =>
    requestAnimationFrame(measureContainer),
  );
  if (containerRef.value) resizeObserver.observe(containerRef.value);
  if (!shouldRestoreFromStore()) {
    store.clear();
    await load(urlQuery());
  }
  initialized.value = true;
  await nextTick();
  requestAnimationFrame(() => requestAnimationFrame(scrollToHashItem));
});

onBeforeUnmount(() => {
  mql.removeEventListener("change", onMqlChange);
  resizeObserver?.disconnect();
});
</script>

<template>
  <div ref="containerRef" class="user-list-container">
    <WindowScroller
      v-if="itemSecondarySize > 0 && items.length > 0"
      ref="scrollerRef"
      class="user-list-virtual"
      :items="items"
      :item-size="itemSize"
      :item-secondary-size="itemSecondarySize"
      :grid-items="gridItems"
      :buffer="scrollerBuffer"
      key-field="key"
      :emit-update="true"
      @update="onScrollerUpdate"
      v-slot="{ item }"
    >
      <template v-if="item">
        <UserCard
          v-if="item.type === 'user'"
          :key="item.key"
          :user="item.data"
        />
        <InfotainmentCard v-else :key="item.key" :infotainment="item.data" />
      </template>
    </WindowScroller>
    <spin-loader v-if="loading"></spin-loader>
    <no-data v-else-if="initialized && items.length === 0">{{
      I18n.t("common.no_result")
    }}</no-data>

    <a
      v-if="initialized && restrict"
      :href="restrict.subscriptionPath"
      class="block text-center mt-2.5 mb-12"
    >
      <connected-members class="mx-2.5 md:mx-auto">
        <div class="wrapper">
          <ul>
            <li v-for="(url, i) in restrict.avatarUrls" :key="i">
              <mini-user-card>
                <img :src="url" alt="" />
              </mini-user-card>
            </li>
          </ul>
          <span class="tag is-on-picture">
            <online-badge class="is-online"></online-badge>
            <span class="text-small font-bold">{{
              restrict.onlineCountLabel
            }}</span>
          </span>
        </div>
      </connected-members>
      <span class="button is-main mx-auto mt-2.5">{{
        I18n.t("common.see_more")
      }}</span>
    </a>
  </div>
</template>

<style scoped>
.user-list-virtual :deep(.vue-recycle-scroller__item-view) {
  padding-top: v-bind("`${gap}px`");
  padding-inline: v-bind("`${gap}px`");

  @media (min-width: 750px) {
    padding-inline: v-bind("`${gap / 2}px`");
  }
}
</style>
